/**
 * Дизайн-токены — единый источник для веб-вьюера (и позже B2B/Flutter).
 * Система «Ясный сигнал» (docs/Design/«Навигация торговых центров будущего (1)»/
 * System.dc.html, ADR 0012): один шрифт Golos Text, единственный цветной герой —
 * ультрасиний acPrimary (только путь и CTA).
 */

export const colors = {
  bgTop: '#F7F8FA',
  bgBottom: '#EDEFF3',
  surfCard: '#FFFFFF',
  surfSunken: '#EDEFF3',
  surfOverlay: 'rgba(13, 15, 20, 0.45)',
  surfMap: '#EDEFF3',
  bdHair: '#E2E6EC',
  bdStrong: '#C6CCD8',
  txPrimary: '#12141A',
  txMuted: '#3A4356',
  txDim: '#7A8294',
  txOnAccent: '#FFFFFF',
  acPrimary: '#2B5CFF',
  acPrimaryDim: '#EEF2FF',
  acOk: '#00C572',
  acOkText: '#00915A',
  acWarn: '#FF7A00',
  acDanger: '#C0392B',
  acAnchor: '#12141A',
  tintOk: 'rgba(0, 197, 114, 0.12)',
  tintAccent: '#F2F5FF',
  progressTrack: '#E2E6EC',
  routeLine: '#2B5CFF',
  routeCasing: '#FFFFFF',
  routeDone: '#BFCBEE',
  mapUnit: '#DCE0E8',
  mapUnitLine: '#C6CCD8',
  mapUnitLabel: '#3A4356',
  mapPoi: '#7A8294',
} as const

/** Тёмный набор — кризис-режим (EMERGENCY) и будущая тёмная тема. */
export const dark = {
  bg: '#0D0F14',
  surf: '#171A21',
  unit: '#232833',
  unitAlt: '#20242E',
  accent: '#5B82FF',
  ok: '#2BD98A',
  border: 'rgba(255, 255, 255, 0.1)',
} as const

/**
 * Палитра «Terra» — премиальный тёмный GIS-дизайн вьюера и приложения
 * (референс docs/Design/modern-gis-logistics-application (4), июль 2026):
 * стеклянные панели, циановый «laser»-акцент, моно-подписи tech-label.
 */
export const terra = {
  abyss: '#07090D',
  graphite: '#0B0E14',
  panel: '#10141C',
  panel2: '#151B26',
  edge: 'rgba(148, 163, 184, 0.14)',
  edgeBright: 'rgba(226, 232, 240, 0.22)',
  ink: '#E6EBF2',
  inkDim: '#8B96A8',
  inkFaint: '#5B6577',
  laser: '#22D3EE',
  laserSoft: 'rgba(34, 211, 238, 0.15)',
  laserEdge: 'rgba(34, 211, 238, 0.4)',
  sapphire: '#3B82F6',
  amber: '#F59E0B',
  emerald: '#34D399',
  rose: '#FB7185',
  violet: '#A78BFA',
  onAccent: '#07090D',
  glassTop: 'rgba(21, 27, 38, 0.92)',
  glassBottom: 'rgba(11, 14, 20, 0.9)',
  glassShadow: '0 1px 0 rgba(255, 255, 255, 0.05) inset, 0 24px 60px -20px rgba(0, 0, 0, 0.75)',
} as const

/** Цвета категорий юнитов/POI в палитре Terra (легенда, маркеры, чипы). */
export const terraCategory = {
  // Розничные категории раскрашены ВСЕ (решение владельца 31.07: «выделять
  // цветом по типу магазина — правильно»): когда цвет был только у части,
  // раскраска выглядела случайным платным выделением. Выключается настройкой
  // площадки category_colors; платное промо — золото, единственное «ярче всех».
  shop: '#38BDF8',
  clothing: '#A78BFA',
  shoes: '#FB923C',
  beauty: '#F472B6',
  jewellery: '#FACC15',
  sport: '#4ADE80',
  home: '#2DD4BF',
  children: '#FB7185',
  grocery: '#A3E635',
  // Еда — одна зелёная семья с cafe: посетитель ищет «где поесть», а не жанр.
  restaurant: '#34D399',
  entertainment: '#67E8F9',
  services: '#94A3B8',
  warehouse: '#F59E0B',
  office: '#818CF8',
  cafe: '#34D399',
  auto: '#FB7185',
  factory: '#A78BFA',
  // Парковка — цветом своего транспорта: тот же голубой, что у маршрута «на
  // авто», и та же охра, что у «фуры» (route-dash.ts). Так человек связывает
  // площадку с тем, на чём он приехал (фидбек владельца 29.07).
  parking: '#38BDF8',
  parking_truck: '#F59E0B',
  checkpoint: '#F87171',
  dock: '#F59E0B',
  loading_dock: '#F59E0B',
  entrance: '#22D3EE',
  info: '#F472B6',
  wc: '#94A3B8',
  qr: '#E2E8F0',
  __default: '#8B96A8',
} as const

/**
 * «Прожектор» — оплаченное выделение (ADR 0019).
 *
 * Цвет выбран НЕ из палитры категорий намеренно: любой категорийный оттенок
 * читался бы как «это склад/кафе», то есть врал бы о сути объекта. Тёплое
 * золото не занято ни одной категорией и работает как свет прожектора —
 * контур и мягкое свечение, а не другая заливка. Так выделение видно, но
 * объект остаётся собой.
 *
 * Насыщенность ниже, чем у laser (маршрут) и safety: реклама не должна
 * спорить взглядом с дорогой и с укрытием.
 */
export const terraPromo = {
  featured: '#FFD27A',
  featuredSoft: 'rgba(255, 210, 122, 0.18)',
  promo: '#FFE6BC',
  promoSoft: 'rgba(255, 230, 188, 0.12)',
} as const

/**
 * Hex-зеркала Aurora для потребителей без OKLCH: MapLibre-стили (csscolorparser
 * не знает oklch) и canvas AR. Посчитаны конверсией OKLCH→sRGB с клампом хромы
 * в гамут (см. docs/Design/APP-DESIGN/TODOS.md §1). Держать в синхроне с aurora!
 */
export const auroraHex = {
  path: '#00c0a0',
  path2: '#00d6ce',
  pathGlow: '#00fad1',
  pathSoft: '#d0fdf0',
  amber: '#fcab00',
  amber2: '#ffc761',
  amberGlow: '#ffd7a0',
  amberSoft: '#fff4e1',
  iris: '#8a7bff',
  iris2: '#b296ff',
  irisGlow: '#bdbcff',
  irisSoft: '#f0f0ff',
  danger: '#fd393f',
  ink: '#091319',
  ink2: '#48555c',
  ink3: '#7b8891',
  paper: '#f3fafc',
  paper2: '#e6f1f4',
  card: '#ffffff',
  line: '#dae3e6',
  land: '#ecf7eb',
  water: '#cceffb',
  building: '#efe8e3',
  road: '#ffffff',
} as const

export const auroraDarkHex = {
  path: '#00d9b5',
  path2: '#00e4dc',
  pathGlow: '#00fad1',
  pathSoft: '#003127',
  amber: '#ffc761',
  amber2: '#ffd896',
  amberGlow: '#ffd7a0',
  amberSoft: '#3c2200',
  iris: '#a9a5ff',
  iris2: '#c8b7ff',
  irisGlow: '#bdbcff',
  irisSoft: '#261d57',
  danger: '#fd393f',
  ink: '#eef7fa',
  ink2: '#aabbc0',
  ink3: '#6e7d83',
  paper: '#02060c',
  paper2: '#06101a',
  card: '#0a1723',
  line: '#1a2834',
  land: '#001611',
  water: '#001829',
  building: '#2b221a',
  road: '#222d34',
} as const

/**
 * Цвета категорий юнитов/POI в тоне Aurora (вьюер). Семантика — как в
 * terraCategory (решения владельца сохранены: все розничные категории
 * раскрашены; еда — одна семья; парковка — цветом своего транспорта: авто —
 * циан path-2, фура — амбер; QR-якорь — амбер, как «точка входа» в дизайне).
 * Оттенки пересчитаны в OKLCH-тон дизайна (L≈0.70–0.74, C≈0.15), чтобы не
 * спорить с маршрутом (path) и целью (amber). b2b-редактор остаётся на
 * terraCategory — там своя тема (Territory CAD).
 */
export const auroraCategory = {
  shop: '#3ba6f5',
  clothing: '#ac8ff8',
  shoes: '#eb883b',
  beauty: '#e57bba',
  jewellery: '#d6b529',
  sport: '#53be70',
  home: '#00beaf',
  children: '#f37986',
  grocery: '#94bb3b',
  restaurant: '#12c281',
  entertainment: '#00c2ca',
  services: '#7b8891',
  warehouse: '#fcab00',
  office: '#7e8ef4',
  cafe: '#12c281',
  auto: '#ea6a64',
  factory: '#b57bdd',
  parking: '#00d6ce',
  parking_truck: '#fcab00',
  checkpoint: '#f3625d',
  dock: '#fcab00',
  loading_dock: '#fcab00',
  entrance: '#00c0a0',
  info: '#8a7bff',
  wc: '#7b8891',
  qr: '#fcab00',
  __default: '#7b8891',
} as const

/** Конфетти «вы на месте» — Aurora-акценты (было: «Ясный сигнал»). */
export const confetti = ['#00c0a0', '#fcab00', '#8a7bff', '#00d6ce'] as const

export const space = { xs: 4, sm: 8, md: 12, lg: 16, xl: 24, xxl: 32 } as const

/** Радиусы по System: кнопки 14–16, карты 20, чипы 999, листы 24. */
export const radius = { chip: 10, button: 16, card: 20, sheet: 24, pill: 999 } as const

/** Тап-цели ≥64px — дизайн-константа продукта (перчатки водителя, CONCEPT §3). */
export const size = { tapMin: 56, tapLarge: 64, routeWidth: 13, routeCasingWidth: 19 } as const

export const shadows = {
  standard: '0 2px 8px rgba(0, 0, 0, 0.10)',
  big: '0 8px 28px rgba(13, 15, 20, 0.16)',
  glow: '0 6px 20px rgba(43, 92, 255, 0.32)',
  sheet: '0 -8px 28px rgba(13, 15, 20, 0.12)',
} as const

export const motion = {
  fastMs: 150,
  calmMs: 300,
  drawMs: 1800,
  dashMs: 1200,
  ease: 'cubic-bezier(0.4, 0, 0.2, 1)',
  easeOut: 'cubic-bezier(0, 0, 0.2, 1)',
} as const

/** Aurora: Inter variable (self-host, OFL) — текст; Space Grotesk — display
 *  (латиница; кириллица падает в Inter — поведение прототипа 1:1). */
export const font = {
  ui: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif",
  /** Display — заголовки/бренд/числа-герои (letter-spacing −0.04em в .font-display). */
  display: "'Space Grotesk', 'Inter', -apple-system, 'Segoe UI', Roboto, sans-serif",
  /** Основной текст. */
  body: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
  /** Моно для кодов (переходно, пока живёт Terra tech-label; уйдёт в D17). */
  mono: "'JetBrains Mono', ui-monospace, SFMono-Regular, Menlo, monospace",
} as const
